import React, { useState } from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";

// Use the provided Animated component definition
// Animated component that applies inline styles based on the animation type
// (Assuming useInView is defined elsewhere in your project)
type AnimationType = "fade-up" | "zoom-in";

type AnimatedProps = {
  animation: AnimationType;
  delay?: number;
  children: React.ReactNode;
};

function Animated({ animation, delay = 0, children }: AnimatedProps) {
  const [ref, visible] = useInView(delay);

  // Base transition styles
  const baseTransition = "opacity 0.8s ease-out, transform 0.8s ease-out";

  // Use React.CSSProperties to include transform property
  let initialStyle: React.CSSProperties = {
    opacity: 0,
    transition: baseTransition,
  };
  if (animation === "fade-up") {
    initialStyle = { ...initialStyle, transform: "translateY(20px)" };
  } else if (animation === "zoom-in") {
    initialStyle = { ...initialStyle, transform: "scale(0.9)" };
  }

  const visibleStyle: React.CSSProperties = {
    opacity: 1,
    transform: "none",
    transition: baseTransition,
  };

  return (
    <Box ref={ref} sx={visible ? visibleStyle : initialStyle}>
      {children}
    </Box>
  );
}

// Dummy useInView hook (replace with your actual hook implementation)
function useInView(delay: number = 0) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(true);
  // For demonstration, we assume it's always visible.
  // Replace this with your actual IntersectionObserver logic.
  return [ref, visible] as const;
}

const featuresData = [
  {
    label: "Diet Tracking",
    subheading: "Track Your Diet",
    description:
      "Log your meals and monitor your caloric intake effortlessly. Snap a photo or enter your meals manually for accurate tracking. You can also add food entried from USDA or your history",
    image: "/images/scan-food.gif",
  },
  {
    label: "Workout Tracking",
    subheading: "Track Your Workouts",
    description:
      "Record your workouts, monitor sets and reps, and track your rest time... ensuring you get a perfect workout, everytime.",
    image: "/images/workout.gif",
  },
  {
    label: "Water Tracking",
    subheading: "Stay Hydrated",
    description:
      "Keep track of your daily water intake with simple UX, ensuring you remain properly hydrated throughout the day.",
    image: "/images/water.gif",
  },
  {
    label: "Weight Tracking",
    subheading: "Monitor Your Weight",
    description:
      "Track your weight with a simple, easy to use chart and log system, helping you stay on top of your weight goals.",
    image: "/images/weight.gif",
  },
  {
    label: "Progress Charts",
    subheading: "Track your progress",
    description:
      "Visualize your progress with clear, clear charts and detailed logs of your progress over time, helping you stay motivated.",
    image: "/images/charts.gif",
  },
];

export default function Features() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedFeature = featuresData[selectedIndex];

  return (
    <Container sx={{ maxWidth: 1200, mt: 8, mb: 8 }}>
      {/* Entry Hero Section */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Animated animation="fade-up">
          <Typography variant="h2" component="h1" fontWeight="bold">
            Avro Features
          </Typography>
        </Animated>
      </Box>

      {/* Menu Section: Animated buttons for each feature */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mb: 6,
          flexWrap: "wrap",
        }}
      >
        {featuresData.map((feature, index) => (
          <Animated animation="fade-up" delay={index * 200} key={feature.label}>
            <Button
              variant={selectedIndex === index ? "contained" : "outlined"}
              onClick={() => setSelectedIndex(index)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: selectedIndex === index ? "bold" : "normal",
              }}
            >
              {feature.label}
            </Button>
          </Animated>
        ))}
      </Box>

      {/* Feature Content Section */}
      <Grid container spacing={4} alignItems="center">
        {/* Image Column */}
        <Grid item xs={12} md={6}>
          <Animated animation="fade-up" delay={200}>
            <Box
              component="img"
              src={selectedFeature.image}
              alt={selectedFeature.subheading}
              sx={{ width: "70%", borderRadius: 2 }}
            />
          </Animated>
        </Grid>
        {/* Text Column */}
        <Grid item xs={12} md={6}>
          <Animated animation="fade-up" delay={400}>
            <Typography
              variant="h4"
              component="h2"
              fontWeight="bold"
              gutterBottom
            >
              {selectedFeature.subheading}
            </Typography>
            <Typography variant="body1">
              {selectedFeature.description}
            </Typography>
          </Animated>
        </Grid>
      </Grid>
    </Container>
  );
}
